/**
 * EVO — vozvrat ekranlarining URL holati.
 *
 * `lib/procurement-search.ts` bilan bir xil qoidalar:
 *   • URL ISHONCHSIZ KIRISH — har maydon tozalanadi (`?page=-5`,
 *     `?status=DROP TABLE` ekranni buzmaydi, default'ga tushadi);
 *   • DEFAULT qiymatlar URL ga YOZILMAYDI — manzil qisqa va ulashsa bo'ladigan;
 *   • oq ro'yxatlar serverda takrorlanmaydi (`sortBy` bu yerda tekshirilmaydi:
 *     noto'g'ri maydon 400 qaytaradi va jadval xato holatini ko'rsatadi).
 *
 * ALOHIDA FAYL: `ops-search.ts` savdo/ombor ekranlarining holatini saqlaydi va
 * u `lib/sales.ts` bilan `lib/inventory.ts` ni tortib keladi. Vozvrat esa
 * o'zining `lib/returns.ts` lug'atlariga tayanadi — ikkalasini bitta faylga
 * qo'shsak, har bir ro'yxat ekrani boshqasining bog'liqliklarini ham
 * yuklab olardi.
 */
import type { SortDir } from './products';
import type { QcResult, ReturnDisposition, ReturnReason, ReturnStatus } from './returns';
import { QC_RESULTS, RETURN_DISPOSITIONS, RETURN_REASONS, RETURN_STATUSES } from './returns';

function positiveInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/** Nol MUMKIN: «kamida 0 kun» — filtrni butunlay olib tashlash bilan bir xil emas. */
function nonNegativeInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Ro'yxatdagi qiymat yoki `undefined` — begona matn jimgina tashlanadi. */
function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** `YYYY-MM-DD` — server ham aynan shu shaklni kutadi. */
function isoDate(value: unknown): string | undefined {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

export interface ReturnSearch {
  readonly page?: number;
  readonly sortBy?: string;
  readonly sortDir?: SortDir;
  readonly q?: string;
  readonly status?: ReturnStatus;
  readonly reasonCode?: ReturnReason;
  readonly customerId?: number;
  readonly from?: string;
  readonly to?: string;
}

export function parseReturnSearch(search: Record<string, unknown>): ReturnSearch {
  const page = positiveInt(search.page);
  const status = oneOf(search.status, RETURN_STATUSES);
  const reasonCode = oneOf(search.reasonCode, RETURN_REASONS);
  const customerId = positiveInt(search.customerId);
  const from = isoDate(search.from);
  const to = isoDate(search.to);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(text(search.sortBy) ? { sortBy: search.sortBy as string } : {}),
    ...(search.sortDir === 'desc' ? { sortDir: 'desc' as SortDir } : {}),
    ...(text(search.q) ? { q: search.q as string } : {}),
    ...(status ? { status } : {}),
    ...(reasonCode ? { reasonCode } : {}),
    ...(customerId !== undefined ? { customerId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
}

export interface QuarantineSearch {
  readonly page?: number;
  readonly q?: string;
  readonly dispositionCode?: ReturnDisposition;
  readonly qcResult?: QcResult;
  readonly minDays?: number;
}

export function parseQuarantineSearch(search: Record<string, unknown>): QuarantineSearch {
  const page = positiveInt(search.page);
  const dispositionCode = oneOf(search.dispositionCode, RETURN_DISPOSITIONS);
  const qcResult = oneOf(search.qcResult, QC_RESULTS);
  const minDays = nonNegativeInt(search.minDays);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(text(search.q) ? { q: search.q as string } : {}),
    ...(dispositionCode ? { dispositionCode } : {}),
    ...(qcResult ? { qcResult } : {}),
    ...(minDays !== undefined && minDays > 0 ? { minDays } : {}),
  };
}
